// One item's page, `stores/<store>/items/<path>` (02-DESIGN/10-state/
// 01-stores-types-and-instances.md § the console: Stores; 01-the-console.md
// § the address; hq decision 0069): its current state — drawn as its type's
// fields where the type's schema is one the form reads — and its history as a
// readable log, newest first, a page at a time with *Older changes*; an item
// whose changes fold to no state says so and is the record of its changes
// (issue 191). The crumb goes back to the store; the store and the item's
// type are named as links to their pages. A path keeps its slashes in the
// address, so the route is a splat. It writes nothing.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { Button, NotAllowed, PageHead, Pending, Pill, useLoad, when } from "../../ui/components.tsx";
import { pageOffered } from "../../roles/offers.ts";
import { Ident, NameLink } from "../../ui/ident.tsx";
import { validWorkloadID } from "../../wire/workloads.ts";
import { schemaView } from "../../ui/schema/SchemaForms.tsx";
import {
  Code,
  validPath,
  validStoreName,
  type Changes,
  type Current,
  type Operation,
} from "../../wire/state.ts";
import { useSurface } from "../surface.tsx";
import { pathOfSplat, storeAddress, typeAddress } from "./addresses.ts";
import { changesLine, HISTORY_PAGE } from "./contents.ts";
import { NO_CURRENT_WORDS, noCurrent } from "./current.ts";
import { schemaAt, typeAt } from "./resolve.ts";

export function ItemPage() {
  const { store = "", "*": splat } = useParams();
  const s = useSurface();
  const path = pathOfSplat(splat);
  if (!validStoreName(store) || !validPath(path)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "stores")}>Stores</Link>}
        title="Not an item"
        line={`"${path}" is not an item's path in "${store}".`}
      />
    );
  }
  return <Item key={`${store}/${path}`} store={store} path={path} />;
}

/** ChangesRead is an item's history as read so far: the newest page first, then each older page on asking. */
type ChangesRead =
  | { state: "pending" }
  | { state: "refused"; err: unknown }
  | { state: "ready"; ops: Operation[]; total: number; next?: number; more: boolean; moreErr?: unknown };

/**
 * Item is one item's current state and its history as a readable log: each
 * change's type, author and time, newest first, a page at a time — the count
 * of every change over the log and *Older changes* while older ones remain
 * (02-DESIGN/10-state/01-stores-types-and-instances.md § the console: Stores;
 * decision 0062).
 */
function Item({ store, path }: { store: string; path: string }) {
  const s = useSurface();
  const [types] = useLoad(`store/${store}/types/items`, () => s.bus.stores().listTypes(store));
  // The schema of the item's type, where its path resolves to one (01 § an instance and its path).
  const schema = schemaAt(types.state === "ready" ? types.value : [], path);
  const typed = types.state === "ready" ? typeAt(types.value, path) : undefined;
  const workloads = pageOffered(s.role, "workloads");
  const authorTo = (author: string | undefined) => (workloads ? authorPage(s.tenant, author) : undefined);
  const [current] = useLoad(`store/${store}/item/${path}`, () => s.bus.stores().get(store, path));
  const [history, setHistory] = useState<ChangesRead>({ state: "pending" });
  // The connection is stable; the surface object is new each render and must not drive a read.
  const bus = s.bus;
  useEffect(() => {
    let live = true;
    bus
      .stores()
      .history(store, path, { limit: HISTORY_PAGE })
      .then(
        (c: Changes) => {
          if (live) {
            setHistory({ state: "ready", ops: c.ops, total: c.total, next: c.next, more: false });
          }
        },
        (err: unknown) => {
          if (live) {
            setHistory({ state: "refused", err });
          }
        },
      );
    return () => {
      live = false;
    };
  }, [bus, store, path]);
  const older = () => {
    if (history.state !== "ready" || history.next === undefined || history.more) {
      return;
    }
    const was = history;
    setHistory({ ...was, more: true, moreErr: undefined });
    s.bus
      .stores()
      .history(store, path, { before: was.next, limit: HISTORY_PAGE })
      .then(
        (c: Changes) => {
          setHistory({
            state: "ready",
            ops: [...was.ops, ...c.ops],
            total: c.total,
            next: c.next,
            more: false,
          });
        },
        (err: unknown) => {
          setHistory({ ...was, more: false, moreErr: err });
        },
      );
  };
  // A not-found current beside a history of changes is a trail, not a failure (issue 191).
  const absence =
    current.state === "refused"
      ? noCurrent(
          isRefusal(current.err, Code.notFound),
          history.state === "pending" ? undefined : history.state === "ready" ? history.total : null,
        )
      : undefined;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "stores")}>Stores</Link>
      <span>/</span>
      <Link to={storeAddress(s.tenant, store)} className="mono">
        {store}
      </Link>
      <span>/</span>
      <span className="mono">{path}</span>
    </>
  );
  return (
    <>
      <PageHead
        crumb={crumb}
        title={<span className="mono">{path}</span>}
        line={
          <>
            {typed !== undefined ? (
              <>
                an item of type{" "}
                <NameLink to={typeAddress(s.tenant, store, typed.name)}>{typed.name}</NameLink>
              </>
            ) : (
              "an untyped item"
            )}{" "}
            in <NameLink to={storeAddress(s.tenant, store)}>{store}</NameLink>
          </>
        }
      />
      <section className="card">
        <div className="field">
          <span className="label">Current</span>
          {current.state === "pending" && <Pending what="Reading" />}
          {current.state === "refused" && absence === undefined && (
            <NotAllowed err={current.err} what="Current" />
          )}
          {absence === "reading" && <Pending what="Reading" />}
          {absence !== undefined && absence !== "reading" && (
            <p className="small muted">{NO_CURRENT_WORDS[absence]}</p>
          )}
          {current.state === "ready" && <CurrentView c={current.value} schema={schema} />}
        </div>
        <div className="field">
          <span className="label">
            History
            {history.state === "ready" && history.total > 0 && (
              <span className="small muted"> &middot; {changesLine(history.ops.length, history.total)}</span>
            )}
          </span>
          {history.state === "pending" && <Pending what="Reading the changes" />}
          {history.state === "refused" && <NotAllowed err={history.err} what="History" />}
          {history.state === "ready" && history.ops.length === 0 && (
            <p className="small muted">No changes recorded.</p>
          )}
          {history.state === "ready" && history.ops.length > 0 && (
            <>
              <div className="list">
                {history.ops.map((op, i) => (
                  <ChangeRow key={op.id ?? `${op.seq ?? i}`} op={op} authorTo={authorTo} />
                ))}
              </div>
              {history.moreErr !== undefined && <NotAllowed err={history.moreErr} what="Older changes" />}
              {history.next !== undefined && (
                <div className="row" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                  <Button size="sm" kind="quiet" onClick={older} disabled={history.more}>
                    {history.more ? "Reading…" : "Older changes"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * CurrentView is an item's current drawn from its type's schema, its fields
 * labelled (03-console/02-forms-from-schemas.md § where it applies: an item's
 * current), with Show as JSON beside it; JSON as before where the type's
 * schema is not one the form reads, or the item has no type.
 */
function CurrentView({ c, schema }: { c: Current; schema: unknown }) {
  const [raw, setRaw] = useState(false);
  const drawn = schemaView(schema, c.state);
  return (
    <>
      {drawn !== undefined && !raw ? drawn : <pre className="json">{JSON.stringify(c.state, null, 2)}</pre>}
      <span className="hint">
        as of change {c.seq}
        {drawn !== undefined && (
          <>
            {" · "}
            <button
              type="button"
              className="text-link"
              onClick={() => {
                setRaw(!raw);
              }}
            >
              {raw ? "Show as fields" : "Show as JSON"}
            </button>
          </>
        )}
      </span>
    </>
  );
}

/**
 * authorPage is the page of a change's author where it has one: a workload's
 * id is a thing with a page; a person's subject is a value (§ conventions: a
 * name is a link).
 */
export function authorPage(slug: string, author: string | undefined): string | undefined {
  return author !== undefined && validWorkloadID(author) ? tenantPath(slug, "workloads", author) : undefined;
}

function ChangeRow({
  op,
  authorTo,
}: {
  op: Operation;
  /** The page of an author, where it has one the role is offered. */
  authorTo: (author: string | undefined) => string | undefined;
}) {
  let data = op.data;
  try {
    data = JSON.stringify(JSON.parse(op.data));
  } catch {
    // shown as written
  }
  return (
    <div className="li" style={{ alignItems: "flex-start" }}>
      <span className="grow">
        <span className="cell-main">
          <Pill tone="info">{op.type}</Pill>
          <span className="small muted">
            {op.author !== undefined && op.author !== "" && (
              <>
                by <Ident value={op.author} label="author" to={authorTo(op.author)} />
              </>
            )}
            {op.ts !== undefined && op.ts !== "" ? ` · ${when(op.ts)}` : ""}
            {op.seq !== undefined ? ` · change ${op.seq}` : ""}
          </span>
        </span>
        {data !== "" && <pre className="json one small">{data}</pre>}
      </span>
    </div>
  );
}
